import React from 'react'

const CheckIcon = () => (
  <svg className='tw-w-5 tw-h-5 tw-text-green tw-flex-shrink-0' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.052-.143z' clipRule='evenodd' />
  </svg>
)

interface PricingPlan {
  title: string
  subtitle: string
  priceNetto: number
  priceBrutto: number
  features: string[]
  isPopular?: boolean
  ctaText?: string
  ctaLink?: string
}

const pricingPlansData: PricingPlan[] = [
  {
    title: 'Starter',
    subtitle: 'Dla Promocji Usług',
    priceNetto: 1219.51,
    priceBrutto: 1500.00,
    features: [
      'Promowanie usług (strona główna)',
      'Dedykowany opiekun konta (w tym prowadzenie profilu, dodawanie zdjęć, artykułów)'
    ],
    ctaText: 'Dowiedz się więcej',
    ctaLink: '#contact'
  },
  {
    title: 'Basic',
    subtitle: 'Dla Promocji Produktów',
    priceNetto: 2032.52,
    priceBrutto: 2500.00,
    features: [
      'Promowanie do 15 produktów (strona główna oraz w katalogu modeli)',
      'Obsługa 30 produktów',
      'Dedykowany opiekun konta (w tym prowadzenie profilu, dodawanie modeli, zdjęć, artykułów)'
    ],
    ctaText: 'Dowiedz się więcej',
    ctaLink: '#contact'
  },
  {
    title: 'Standard',
    subtitle: 'Dla Promocji Kolekcji',
    priceNetto: 3252.00,
    priceBrutto: 4000.00,
    features: [
      'Promowanie do 50 produktów (strona główna oraz w katalogu modeli)',
      'Obsługa 100 produktów',
      'Dedykowany opiekun konta',
      'Statystyki',
      'Raporty cykliczne na e-mail'
    ],
    ctaText: 'Dowiedz się więcej',
    ctaLink: '#contact'
  },
  {
    title: 'Professional',
    subtitle: 'Dla Promocji Marki',
    priceNetto: 4878.00,
    priceBrutto: 6000.00,
    features: [
      'Promowanie marki na stronie głównej i w katalogu modeli',
      'Promowanie do 100 produktów',
      'Obsługa 200 produktów',
      'Dedykowany opiekun konta',
      'Statystyki i raporty cykliczne na e-mail',
      'Logo na stronie głównej',
      'Wyróżnione miejsce w katalogu'
    ],
    ctaText: 'Dowiedz się więcej',
    isPopular: true,
    ctaLink: '#contact'
  }
]

interface PlanCardProps {
  plan: PricingPlan
}

const PlanCard: React.FC<PlanCardProps> = ({ plan }) => {
  return (
    <div className={`
      tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-6 md:tw-p-8 tw-flex tw-flex-col tw-h-full tw-relative
      ${plan.isPopular ? 'tw-border-2 tw-border-green' : 'tw-border tw-border-slate-200'}
    `}
    >
      {plan.isPopular && (
        <div className='tw-absolute tw-top-0 tw-right-6 tw--translate-y-1/2 tw-bg-green tw-text-white tw-text-xs tw-font-semibold tw-px-3 tw-py-1 tw-rounded-full'>
          Popularny
        </div>
      )}
      <div className='tw-flex-grow'>
        <p className='tw-text-sm tw-font-semibold tw-text-pink-600  tw-mb-1'>{plan.subtitle}</p>
        <h3 className='tw-text-2xl tw-font-bold tw-text-teal tw-mb-3'>{plan.title}</h3>

        <div className='tw-mb-6'>
          <div className='tw-flex tw-items-baseline'>
            <span className='tw-text-4xl tw-font-extrabold tw-text-teal'>{plan.priceBrutto.toFixed(2)}</span>
            <span className='tw-text-slate-500 tw-ml-1'>zł</span>
          </div>
          <p className='tw-text-xs tw-text-slate-400'>brutto/rok ({plan.priceNetto.toFixed(2)} zł netto)</p>
        </div>

        <ul className='tw-space-y-3 tw-mb-8'>
          {plan.features.map((feature, featureIndex) => (
            <li key={featureIndex} className='tw-flex tw-items-start'>
              <CheckIcon />
              <span className='tw-ml-3 tw-text-sm tw-text-slate-600'>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      {/* tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-bg-green hover:tw-bg-opacity-90 tw-text-white tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base" */}
      {plan.ctaText && plan.ctaLink && (
        <div className='tw-mt-auto'>
          <a
            href={plan.ctaLink}
            className={`
                    tw-w-full tw-flex tw-items-center tw-justify-center tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-bg-green hover:tw-bg-opacity-90 tw-text-white tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-text-white tw-bg-[#47a145] hover:tw-opacity-90`}
          >
            {plan.ctaText}
          </a>
        </div>
      )}
    </div>
  )
}

const PricingSection: React.FC = () => {
  return (
    <div id='pricing'>
      <section className='tw-bg-slate-50 tw-pt-12 md:tw-pt-20 lg:tw-pt-24 tw-pb-0'>
        <div className='tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8'>
          <div className='tw-max-w-3xl tw-mx-auto tw-text-center tw-mb-12 md:tw-mb-16'>
            <h2 className='tw-text-sm tw-font-semibold tw-uppercase tw-text-green tw-mb-2'>Cennik dla Firm</h2>
            <p className='tw-text-3xl md:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-teal'>
              Rozwiązania dopasowane do Twoich potrzeb
            </p>
            <p className='tw-mt-4 tw-text-lg tw-text-slate-600'>
              Wybierz plan, który najlepiej odpowiada wielkości i wymaganiom Twojego biznesu.
            </p>
          </div>

          <div className='tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-6 md:tw-gap-8'>
            {pricingPlansData.map((plan, index) => (
              <PlanCard key={index} plan={plan} />
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default PricingSection
